import { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import { saveWithAction } from '../actions/trySaveMessage';
import upgradeCompose from '../actions/upgradeCompose';
import datapoints from '../datapoints';
import ComposeTarget from '../store/schema/ComposeTarget';
import ComposeViewState, { ComposeOperation } from '../store/schema/ComposeViewState';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import { createReplyFromReplyItem, createReplyItem } from '../utils/createReply';
import findInlineComposeViewState from '../utils/findInlineComposeViewState';
import getQuotedBodyForSmimeItemReply from '../utils/getQuotedBodyForSmimeItemReply';
import tryGetSmimeViewStateForResponse from '../utils/tryGetSmimeViewStateForResponse';
import getExtendedPropertyValue from '../utils/getExtendedPropertyValue';
import { logReplyInfo } from '../utils/logReplyInfo';
import { lazyShouldAddinsForceFullCompose } from 'owa-addins-core';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { AttachmentFileAttributes } from 'owa-attachment-file-types';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { userMailInteractionAction } from 'owa-mail-actions';
import { ClientItem } from 'owa-mail-store';
import { getStore, InstrumentationContext } from 'owa-mail-store';
import { isDeepLink } from 'owa-popout';
import Message from 'owa-service/lib/contract/Message';
import getAttachmentsForSmimeResponse from 'owa-smime/lib/utils/getAttachmentsForSmimeResponse';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { isSxSDisplayed } from 'owa-sxs-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { mutatorAction } from 'satcheljs';
import { isSmimeAttachment } from 'owa-attachment-model-store';

const LABEL_PROPERTY_NAME = 'msip_labels';

export interface ReplyToMessageCommonOptions {
    isReplyAll: boolean;
    useFullCompose: boolean;
    actionSource?: ActionSource;
    fullComposeTarget?: ComposeTarget;
    attachmentFiles?: AttachmentFileAttributes[];
    conversationId?: string;
    instrumentationContexts: InstrumentationContext[];
    initialComposeLogTraceId?: string;
    body?: string;
    groupId?: string;
    suppressServerMarkReadOnReplyOrForward?: boolean;
}

export interface ReplyToMessageAndCreateViewStateOptions extends ReplyToMessageCommonOptions {
    referenceItemOrId: string | Message;
}

export interface ReplyToMessageActionOptions extends ReplyToMessageCommonOptions {
    referenceItemId: string;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
}

// Remove groupId from ReplyToMessageCommonOptions and add replyToMessage-specific properties
export type ReplyToMessageOptions = Pick<
    ReplyToMessageCommonOptions,
    Exclude<keyof ReplyToMessageCommonOptions, 'groupId'>
> & {
    referenceItemId: ClientItemId;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
};

export async function replyToMessageAndCreateViewState(
    options: ReplyToMessageAndCreateViewStateOptions
): Promise<ComposeViewState> {
    let viewState: ComposeViewState = null;
    let fullComposeTarget = options.fullComposeTarget;
    const {
        referenceItemOrId,
        isReplyAll,
        useFullCompose,
        conversationId,
        initialComposeLogTraceId,
        groupId,
        body,
        instrumentationContexts,
        attachmentFiles,
        actionSource,
        suppressServerMarkReadOnReplyOrForward,
    } = options;

    const tabViewEnabled = isTabViewEnabled();

    if (fullComposeTarget === undefined) {
        fullComposeTarget = isSxSDisplayed()
            ? ComposeTarget.SxS
            : isDeepLink() || !tabViewEnabled
            ? ComposeTarget.PrimaryReadingPane
            : ComposeTarget.SecondaryTab;
    }

    if (!useFullCompose || !tabViewEnabled) {
        const existingViewState = findInlineComposeViewState(conversationId);
        if (existingViewState) {
            await trySaveAndCloseCompose(existingViewState);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (findInlineComposeViewState(conversationId)) {
                return Promise.resolve(null);
            }
        }
    }

    // If traceId is not passed create new one so we can correlate between all replies and sent
    const composeTraceId = initialComposeLogTraceId || getGuid();

    let referenceItem: Message;

    if (typeof referenceItemOrId === 'string') {
        const mailStore = getStore();
        const items = mailStore && mailStore.items;

        if (items && items.has(referenceItemOrId)) {
            referenceItem = items.get(referenceItemOrId) as Message;
        }
    } else {
        referenceItem = referenceItemOrId as Message;
    }

    if (referenceItem) {
        // create a reply item so we can initialize composeInitProps from it
        // before creating the reply.
        const replyItem = await createReplyItem(referenceItem, isReplyAll);
        const composeOperation = isReplyAll ? ComposeOperation.ReplyAll : ComposeOperation.Reply;
        const smimeViewState = await tryGetSmimeViewStateForResponse(
            referenceItem as ClientItem,
            true /* isReplyOrReplyAll */
        );

        /**
         * Below check decides whether the replying mail will be an S/MIME mail
         * or not. This condition has to be first one in the below if..elseIf blocks
         * because if S/MIME admin settings are enabled then by default every reply mail will
         * be an S/MIME mail. Also, if the message is an attachment of an S/MIME mail, we need to
         * ensure that we treat it like a S/MIME mail and not make the server call since server has no info about this mail.
         */
        if (
            !groupId &&
            isFeatureEnabled('cmp-smime') &&
            (isSmimeAttachment(referenceItem.ItemId.Id) ||
                isSmimeEnabledInViewState(smimeViewState))
        ) {
            const quotedBody = await getQuotedBodyForSmimeItemReply(
                referenceItem,
                composeOperation
            );
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: replyItem.NewBodyContent.BodyType,
                subject: replyItem.Subject,
                newContent: body,
                to: replyItem.ToRecipients,
                cc: replyItem.CcRecipients,
                bcc: replyItem.BccRecipients,
                referenceItemId: referenceItem.ItemId,
                referenceItemDocumentId: replyItem.ReferenceItemDocumentId,
                isInlineCompose: false /** Smime reply is not supported in inline compose */,
                preferAsyncSend: false,
                useSmartResponse: false,
                conversationId: conversationId,
                logTraceId: composeTraceId,
                isPopout: isDeepLink(),
                IRMData: referenceItem.RightsManagementLicenseData,
                // TODO: fetch NormalizedBody from server in case its missing WI: 45155
                quotedBody,
                attachments: getAttachmentsForSmimeResponse(
                    referenceItem.Attachments,
                    false /* isForward */
                ),
                smimeViewState,
            };

            viewState = await prepareComposeViewState(
                composeInitProps,
                null /* groupId */,
                attachmentFiles
            );
            await openComposeWithViewState(viewState, fullComposeTarget);
        } else if (conversationId && !useFullCompose) {
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: replyItem.NewBodyContent.BodyType,
                subject: replyItem.Subject,
                newContent: body,
                to: replyItem.ToRecipients,
                cc: replyItem.CcRecipients,
                bcc: replyItem.BccRecipients,
                referenceItemId: referenceItem.ItemId,
                referenceItemDocumentId: replyItem.ReferenceItemDocumentId,
                conversationId: conversationId,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                logTraceId: composeTraceId,
                labelStringInReferenceItem: getExtendedPropertyValue(
                    referenceItem,
                    null, // Property Tag
                    LABEL_PROPERTY_NAME
                ),
            };

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);
            await openComposeWithViewState(viewState, ComposeTarget.PrimaryReadingPane);

            if (!groupId) {
                // Create the draft in background to prepare for upConvert
                saveWithAction(
                    viewState,
                    createReplyFromReplyItem(
                        replyItem,
                        null,
                        suppressServerMarkReadOnReplyOrForward
                    ).then(message => {
                        if (message) {
                            updateViewStateAfterReplyCreated(viewState, message);
                        }
                    })
                );
            }
        } else {
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: replyItem.NewBodyContent.BodyType,
                subject: replyItem.Subject,
                newContent: body,
                to: replyItem.ToRecipients,
                cc: replyItem.CcRecipients,
                bcc: replyItem.BccRecipients,
                referenceItemId: referenceItem.ItemId,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                useSmartResponse: true, // Set useSmartResponse to true in case we can't get quoted body
                logTraceId: composeTraceId,
                isPopout: isDeepLink(),
                labelStringInReferenceItem: getExtendedPropertyValue(
                    referenceItem,
                    null,
                    LABEL_PROPERTY_NAME
                ),
            };

            if (!isFeatureEnabled('cmp-upgradeCompose')) {
                const message = await createReplyFromReplyItem(
                    replyItem,
                    groupId,
                    suppressServerMarkReadOnReplyOrForward
                );
                if (message) {
                    composeInitProps.itemId = message.ItemId;
                    composeInitProps.quotedBody = message.Body.Value;
                    composeInitProps.attachments = message.Attachments;
                    composeInitProps.useSmartResponse = false; // Quoted body is loaded, no need to use smart response now
                }
            }

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);
            await openComposeWithViewState(viewState, fullComposeTarget);

            if (isFeatureEnabled('cmp-upgradeCompose')) {
                upgradeCompose(
                    viewState,
                    createReplyFromReplyItem(
                        replyItem,
                        groupId,
                        suppressServerMarkReadOnReplyOrForward
                    )
                );
            }
        }

        logReplyInfo(replyItem, composeTraceId, actionSource, referenceItem.DateTimeReceived);

        isReplyAll
            ? userMailInteractionAction('ReplyAll', instrumentationContexts)
            : userMailInteractionAction('Reply', instrumentationContexts);
    }

    return viewState;
}

// We need to have two entrance for reply and replyAll since they are using different datapoint name
const replyToMessageAction = createLegacyActionWithDatapoint(
    'replyToMessage',
    datapoints.MailComposeReply
)(async function replyToMessageAction(options: ReplyToMessageActionOptions) {
    const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
        ...(options as ReplyToMessageCommonOptions),
        referenceItemOrId: options.referenceItemId,
        isReplyAll: false,
    });

    if (options.onComposeViewStateCreated) {
        options.onComposeViewStateCreated(composeViewState);
    }
});

const replyAllToMessageAction = createLegacyActionWithDatapoint(
    'replyAllToMessage',
    datapoints.MailComposeReplyAll
)(async function replyAllToMessageAction(options: ReplyToMessageActionOptions) {
    const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
        ...(options as ReplyToMessageCommonOptions),
        referenceItemOrId: options.referenceItemId,
        isReplyAll: true,
    });

    if (options.onComposeViewStateCreated) {
        options.onComposeViewStateCreated(composeViewState);
    }
});

export default async function replyToMessage(options: ReplyToMessageOptions): Promise<void> {
    const mailboxInfo = options.referenceItemId.mailboxInfo;
    const groupId =
        mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox ? mailboxInfo.mailboxId : null;

    if (isFeatureEnabled('rp-addins')) {
        const shouldAddinsForceFullCompose = await lazyShouldAddinsForceFullCompose.import();
        if (
            (options.fullComposeTarget === ComposeTarget.PrimaryReadingPane ||
                options.fullComposeTarget === undefined) &&
            shouldAddinsForceFullCompose()
        ) {
            options.useFullCompose = true;
            options.fullComposeTarget = ComposeTarget.SecondaryTab;
        }
    }

    const parameters = {
        ...(options as ReplyToMessageCommonOptions),
        referenceItemId: options.referenceItemId.Id,
        groupId: groupId,
    };

    if (options.isReplyAll) {
        return await replyAllToMessageAction(parameters);
    } else {
        return await replyToMessageAction(parameters);
    }
}

const updateViewStateAfterReplyCreated = mutatorAction('updateViewStateAfterReplyCreated', function(
    viewState: ComposeViewState,
    message: Message
) {
    viewState.itemId = message.ItemId;
    viewState.subject = message.Subject;
    viewState.quotedBody = message.Body.Value;
});
